import { type BulletinSectionKey } from "@/components/bulletins/bulletin-data";

/** Read a section row's persisted on/off flag from config_json. Absent or
 * non-boolean = enabled (backward compatible). */
export function getConfigEnabled(config: Record<string, unknown> | null | undefined): boolean {
  return typeof config?.enabled === "boolean" ? (config.enabled as boolean) : true;
}

/** A section TYPE is disabled iff at least one instance of that type exists in
 * the rows AND every instance of that type is disabled. Used by the member
 * weekly view, which collapses to one instance per type. */
export function computeDisabledSectionTypes(
  rows: Array<{ type: BulletinSectionKey; config_json?: Record<string, unknown> | null }>,
): Set<BulletinSectionKey> {
  const byType = new Map<BulletinSectionKey, { total: number; disabled: number }>();
  for (const row of rows) {
    const tally = byType.get(row.type) ?? { total: 0, disabled: 0 };
    tally.total += 1;
    if (!getConfigEnabled(row.config_json)) tally.disabled += 1;
    byType.set(row.type, tally);
  }
  const result = new Set<BulletinSectionKey>();
  for (const [type, { total, disabled }] of byType) {
    if (total > 0 && disabled === total) result.add(type);
  }
  return result;
}

/**
 * Deduped event ids referenced by a bulletin's own items, limited to items
 * whose OWN section is enabled. A disabled section's items are excluded from
 * sectionOrder and from the public snapshot (BulletinBuilderPage.tsx's
 * bulletinSnapshot) alike -- an event belonging to one is never expected to
 * appear in either place, so a caller comparing "what's selected" against
 * "what's in the snapshot" (BulletinDetailView.tsx's
 * snapshotCoversSelectedIds) must exclude it too, or a perfectly public,
 * fully-covered bulletin with a disabled events section reads as incomplete
 * (Codex P2, PR #6383 round 22).
 */
export function selectEnabledSectionEventIds(
  sections: Array<{ id: string; config_json?: Record<string, unknown> | null }>,
  items: Array<{ bulletin_section_id?: string | null; event_id?: string | null }>,
): string[] {
  const enabledSectionIds = new Set(
    sections.filter((section) => getConfigEnabled(section.config_json)).map((section) => section.id),
  );
  return Array.from(
    new Set(
      items
        .filter((item) => enabledSectionIds.has(item.bulletin_section_id ?? ""))
        .map((item) => item.event_id)
        .filter((eventId): eventId is string => Boolean(eventId)),
    ),
  );
}
